import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { FinanceStatCard } from '@/modules/finance/components/FinanceStatCard';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Download,
  FileText,
  CreditCard,
  Clock,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Calendar,
  RefreshCw } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusConfig = {
  pending: { label: 'Pending', class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400' },
  paid: { label: 'Paid', class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400' },
  overdue: { label: 'Overdue', class: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400' },
  partial: { label: 'Partial', class: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400' }
};

export const AccountsPayable = () => {const { t } = useTranslation("finance");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [bills, setBills] = useState([]);
  const [paymentRuns, setPaymentRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [billsRes, runsRes] = await Promise.all([
      fetch(`${API_URL}/api/finance/vendor-bills`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/finance/payment-runs`, { headers: getAuthHeaders() })]
      );
      if (!billsRes.ok || !runsRes.ok) throw new Error('Failed to fetch data');
      const [billsData, runsData] = await Promise.all([billsRes.json(), runsRes.json()]);
      setBills(billsData);
      setPaymentRuns(runsData);
    } catch (err) {
      toast.error('Failed to load accounts payable data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stats = {
    totalOutstanding: bills.filter((b) => b.status !== 'paid').reduce((sum, b) => sum + (b.amount - b.paid_amount), 0),
    overdueAmount: bills.filter((b) => b.status === 'overdue').reduce((sum, b) => sum + b.amount, 0),
    dueThisWeek: bills.filter((b) => b.status === 'pending').reduce((sum, b) => sum + b.amount, 0),
    paidThisMonth: bills.filter((b) => b.status === 'paid').reduce((sum, b) => sum + b.amount, 0)
  };

  const filteredBills = bills.filter((bill) => {
    if (!searchTerm) return true;
    return bill.bill_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bill.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="finance-accounts-payable">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("accountsPayable", "Accounts Payable")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageVendorBills", "Manage vendor bills and payments")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2" onClick={() => toast.info('Export started...')}>
            <Download className="h-4 w-4" />{t("exportCsv", "Export CSV")}

          </Button>
          <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/finance/bills/new')}>
            <Plus className="h-4 w-4" />{t("recordBill", "Record Bill")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <FinanceStatCard
          title="Total Outstanding"
          value={stats.totalOutstanding}
          icon={CreditCard}
          color="blue"
          prefix="$" />
        
        <FinanceStatCard
          title="Overdue"
          value={stats.overdueAmount}
          icon={AlertTriangle}
          color="red"
          prefix="$" />
        
        <FinanceStatCard
          title="Due This Week"
          value={stats.dueThisWeek}
          icon={Clock}
          color="gold"
          prefix="$" />
        
        <FinanceStatCard
          title="Paid This Month"
          value={stats.paidThisMonth}
          icon={CheckCircle2}
          color="emerald"
          prefix="$" />
        
      </div>

      {/* Tabs */}
      <Tabs defaultValue="bills" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="bills" className="gap-2">
            <FileText className="h-4 w-4" />{t("vendorBills", "Vendor Bills")}

          </TabsTrigger>
          <TabsTrigger value="payments" className="gap-2">
            <DollarSign className="h-4 w-4" />{t("paymentRuns", "Payment Runs")}

          </TabsTrigger>
        </TabsList>

        <TabsContent value="bills" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="text-lg">{t("vendorBills", "Vendor Bills")}</CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search bills..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10" />
                  
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("bill", "Bill #")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("vendor", "Vendor")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("billDate", "Bill Date")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("dueDate", "Due Date")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("amount", "Amount")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("balance", "Balance")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBills.map((bill) =>
                    <tr key={bill.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-4 px-4">
                          <span className="font-mono text-sm font-medium">{bill.bill_number}</span>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium text-sm">{bill.vendor_name}</p>
                            {bill.po_reference &&
                          <p className="text-xs text-muted-foreground">{t("po", "PO:")}{bill.po_reference}</p>
                          }
                          </div>
                        </td>
                        <td className="py-4 px-4 text-sm">{bill.bill_date}</td>
                        <td className="py-4 px-4 text-sm">{bill.due_date}</td>
                        <td className="py-4 px-4 text-right font-mono">
                          ${bill.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-4 px-4 text-right font-mono font-medium">
                          ${(bill.amount - bill.paid_amount)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={statusConfig[bill.status]?.class || statusConfig.pending.class}>
                            {statusConfig[bill.status]?.label || bill.status}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-right">
                          {bill.status !== 'paid' &&
                        <Button size="sm" variant="outline" onClick={() => navigate(`/finance/payments/new?bill=${bill.id}`)}>{t("payNow", "Pay Now")}</Button>
                        }
                          {bill.status === 'paid' &&
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/finance/bills/${bill.id}`)}>{t("view", "View")}</Button>
                        }
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {filteredBills.length === 0 &&
              <div className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t("noBillsFound", "No bills found")}</h3>
                  <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search criteria")}</p>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">{t("paymentRuns", "Payment Runs")}</CardTitle>
              <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/finance/payment-runs/new')}>
                <Plus className="h-4 w-4" />{t("schedulePaymentRun", "Schedule Payment Run")}

              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("runId", "Run ID")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("date", "Date")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("vendors", "Vendors")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("totalAmount", "Total Amount")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentRuns.map((run) =>
                    <tr key={run.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-4 px-4">
                          <span className="font-mono text-sm font-medium">{run.id}</span>
                        </td>
                        <td className="py-4 px-4 text-sm">{run.date}</td>
                        <td className="py-4 px-4 text-center">{run.vendor_count}</td>
                        <td className="py-4 px-4 text-right font-mono font-medium">
                          ${run.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={
                        run.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-blue-100 text-blue-700'
                        }>
                            {run.status?.charAt(0).toUpperCase() + run.status?.slice(1)}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/finance/payment-runs/${run.id}`)}>{t("viewDetails", "View Details")}</Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};